/**
 * useTextureManager Hook
 * 
 * Manage textures:
 * - Extract textures dari model
 * - Upload replacement textures (image/video)
 * - Track video textures
 * - Update texture list
 */

import { useState, useCallback, useRef } from 'react'

export function useTextureManager(modelRef) {
  const [textures, setTextures] = useState([])
  const [selectedTexture, setSelectedTexture] = useState(null)
  const [videoSyncMode, setVideoSyncMode] = useState('loop')
  const [videosBuffering, setVideosBuffering] = useState(0)
  
  const fileInputRefs = useRef({})
  const videoElementsRef = useRef({})

  // Update textures list
  const updateTexturesList = useCallback(() => {
    if (!modelRef.current || !window.THREE_MODULE) return

    const textureList = []
    const textureMap = new Map()

    modelRef.current.traverse((child) => {
      if (child.isMesh) {
        const mats = Array.isArray(child.material)
          ? child.material
          : [child.material]

        mats.forEach((mat) => {
          if (mat) {
            const textureTypes = [
              'map',
              'normalMap',
              'roughnessMap',
              'metalnessMap',
              'aoMap',
              'emissiveMap',
            ]

            textureTypes.forEach((texType) => {
              if (mat[texType]) {
                const texture = mat[texType]
                const textureId = texture.uuid

                if (!textureMap.has(textureId)) {
                  const videoData = videoElementsRef.current[textureId]
                  const isVideo =
                    texture.isVideoTexture || (videoData && videoData.video)

                  textureMap.set(textureId, {
                    id: textureId,
                    name: texture.name || `${texType} ${textureMap.size + 1}`,
                    type: texType,
                    texture: texture,
                    material: mat,
                    meshName: child.name || 'Unnamed Mesh',
                    isVideo: isVideo,
                    videoDuration: videoData?.videoDuration,
                    syncMode: videoData?.syncMode || 'loop',
                  })
                }
              }
            })
          }
        })
      }
    })

    textureMap.forEach((texData) => textureList.push(texData))
    setTextures(textureList)
  }, [modelRef])

  // Handle texture upload (image or video)
  const handleTextureUpload = useCallback(
    (textureData, file) => {
      if (!file || !window.THREE_MODULE) return

      const THREE = window.THREE_MODULE
      const fileExtension = file.name.split('.').pop().toLowerCase()
      const isVideo = ['mp4', 'webm', 'ogg', 'mov'].includes(fileExtension)

      if (isVideo) {
        // Video texture handling
        const videoUrl = URL.createObjectURL(file)
        const video = document.createElement('video')
        video.src = videoUrl
        video.crossOrigin = 'anonymous'
        video.loop = false
        video.muted = true
        video.playsInline = true
        video.preload = 'auto'
        video.load()

        video.addEventListener('loadedmetadata', () => {
          setTimeout(() => {
            let duration = video.duration

            if (!isFinite(duration) || duration <= 0) {
              console.error('Invalid video duration:', duration)
              alert('Video file is invalid or corrupted.')
              return
            }

            const videoTexture = new THREE.VideoTexture(video)
            videoTexture.wrapS = textureData.texture.wrapS
            videoTexture.wrapT = textureData.texture.wrapT
            videoTexture.repeat.copy(textureData.texture.repeat)
            videoTexture.offset.copy(textureData.texture.offset)
            videoTexture.rotation = textureData.texture.rotation
            videoTexture.center.set(0.5, 0.5)

            if (
              textureData.type === 'map' ||
              textureData.type === 'emissiveMap'
            ) {
              videoTexture.encoding = THREE.sRGBEncoding
            }

            videoTexture.needsUpdate = true

            // Store video element
            videoElementsRef.current[textureData.id] = {
              video: video,
              videoDuration: duration,
              textureId: textureData.id,
              isSynced: true,
              syncMode: videoSyncMode,
              isBuffering: false,
              lastSeekTime: 0,
            }

            // Pre-buffer video
            setVideosBuffering((prev) => prev + 1)
            videoElementsRef.current[textureData.id].isBuffering = true

            video.muted = true
            video.currentTime = 0
            video
              .play()
              .then(() => {
                setTimeout(
                  () => {
                    video.pause()
                    video.currentTime = 0
                    if (videoElementsRef.current[textureData.id]) {
                      videoElementsRef.current[textureData.id].isBuffering = false
                    }
                    setVideosBuffering((prev) => Math.max(0, prev - 1))
                  },
                  Math.min(duration * 1000, 2000)
                )
              })
              .catch((err) => {
                console.warn('Pre-buffer failed:', err)
                if (videoElementsRef.current[textureData.id]) {
                  videoElementsRef.current[textureData.id].isBuffering = false
                }
                setVideosBuffering((prev) => Math.max(0, prev - 1))
              })

            // Replace texture
            textureData.material[textureData.type] = videoTexture
            textureData.material.needsUpdate = true

            console.log(`Video texture replaced: ${textureData.name}`)
            updateTexturesList()
          }, 100)
        })
      } else {
        // Image texture handling
        const reader = new FileReader()

        reader.onload = (e) => {
          const textureLoader = new THREE.TextureLoader()
          textureLoader.load(
            e.target.result,
            (newTexture) => {
              newTexture.wrapS = textureData.texture.wrapS
              newTexture.wrapT = textureData.texture.wrapT
              newTexture.repeat.copy(textureData.texture.repeat)
              newTexture.offset.copy(textureData.texture.offset)
              newTexture.rotation = textureData.texture.rotation
              newTexture.center.set(0.5, 0.5)

              if (
                textureData.type === 'map' ||
                textureData.type === 'emissiveMap'
              ) {
                newTexture.encoding = THREE.sRGBEncoding
              }

              newTexture.needsUpdate = true

              textureData.material[textureData.type] = newTexture
              textureData.material.needsUpdate = true

              console.log(`Texture replaced: ${textureData.name}`)
              updateTexturesList()
            },
            undefined,
            (error) => {
              console.error('Error loading texture:', error)
              alert('Failed to load texture.')
            }
          )
        }

        reader.readAsDataURL(file)
      }
    },
    [videoSyncMode, updateTexturesList]
  )

  return {
    textures,
    setTextures,
    selectedTexture,
    setSelectedTexture,
    videoSyncMode,
    setVideoSyncMode,
    videosBuffering,
    setVideosBuffering,
    fileInputRefs,
    videoElementsRef,
    updateTexturesList,
    handleTextureUpload,
  }
}

